import React, { useContext } from "react";
import style from "./index.module.scss";
import { Button, message, Modal, Table, Tooltip } from "antd";
import { ColumnProps, SorterResult, SortOrder } from "antd/lib/table/interface";
import StuTableColumn from "components/CourseComponents/StuTableColumn";
import { CourseContext } from "../../../Store";
import { useHistory } from "react-router-dom";
import { collectExaminationPaper, getMarkListItem } from "service/exercise";
import moment from "moment";
import {
    sorterAtom,
    useUpdateMarkList
} from "service/stores/Exercise/Students";
import { useAtom } from "jotai";
import { errorMessage, isTrue } from "service/service";

interface StudentsTableProps {
    data: getMarkListItem[];
    exerciseId: string;
}

export default function StudentsTable({
    data,
    exerciseId
}: StudentsTableProps) {
    const { basename, course } = useContext(CourseContext);
    const history = useHistory();
    const sortDirections: SortOrder[] = ["descend", "ascend"];
    const [sorter, setSorter] = useAtom(sorterAtom);
    const update = useUpdateMarkList();

    function getSortOrder(field: string) {
        return sorter.field === field ? sorter.order : false;
    }

    async function onCollect(studentNo: string) {
        Modal.confirm({
            content: "要强制收卷吗?",
            onOk: async () => {
                try {
                    await collectExaminationPaper(
                        exerciseId,
                        studentNo,
                        course.courseId
                    );

                    update();
                    message.success("收卷成功!");
                } catch (e) {
                    Modal.error(errorMessage(e));
                }
            }
        });
    }

    const columns: ColumnProps<getMarkListItem>[] = [
        {
            title: "序号",
            dataIndex: "index",
            align: "center",
            render: (text, item, index) => index + 1
        },
        {
            title: "班级",
            dataIndex: "className",
            align: "center",
            render: text => (
                <div className={style.column}>
                    <Tooltip placement="topLeft" title={text}>
                        {text}
                    </Tooltip>
                </div>
            )
        },
        {
            title: "学生信息",
            dataIndex: "studentNo",
            align: "left",
            render: (val, item) => (
                <StuTableColumn name={item.name} stuNo={val} />
            )
        },
        {
            title: "参与状态",
            dataIndex: "joinStatus",
            align: "center",
            render: text => <div>{text === "0" ? "未参与" : "已参与"}</div>
        },
        {
            title: "提交时间",
            dataIndex: "submitTime",
            align: "center",
            sortDirections: sortDirections,
            render: text => (
                <div>{text ? moment(text).format("MM-DD HH:mm") : "--"}</div>
            ),
            sorter: true,
            sortOrder: getSortOrder("submitTime")
        },
        {
            title: "客观题得分",
            dataIndex: "objScore",
            align: "center",
            sorter: true,
            sortOrder: getSortOrder("objScore")
        },
        {
            title: "主观题得分",
            dataIndex: "subScore",
            align: "center",
            sorter: true,
            sortOrder: getSortOrder("subScore")
        },
        {
            title: "总得分",
            dataIndex: "score",
            align: "center",
            sorter: true,
            sortOrder: getSortOrder("score")
        },
        {
            title: "操作",
            align: "center",
            render: (_, item) => {
                return (
                    <div className={style.operation}>
                        {item.joinStatus === "0" &&
                            (isTrue(item.paperFlags) ? (
                                <Button
                                    type="link"
                                    onClick={() => onCollect(item.studentNo)}>
                                    收卷
                                </Button>
                            ) : (
                                <span>未参与</span>
                            ))}

                        {item.joinStatus === "1" && item.markStatus === "0" && (
                            <Button
                                type="link"
                                onClick={() => gotoReview(item.studentNo)}>
                                批阅
                            </Button>
                        )}

                        {item.joinStatus === "1" && item.markStatus === "1" && (
                            <Button
                                type="link"
                                onClick={() => gotoReview(item.studentNo)}>
                                重批
                            </Button>
                        )}
                    </div>
                );
            }
        }
    ];

    function gotoReview(studentNo: string) {
        history.push(
            `${basename}/exercise/students/${exerciseId}/review/${studentNo}`
        );
    }

    function onChange(
        pagination: unknown,
        filters: unknown,
        sorter: SorterResult<any>
    ) {
        setSorter(sorter);
    }

    return (
        <div className={style.root}>
            <Table
                columns={columns}
                dataSource={data}
                rowKey="studentNo"
                pagination={false}
                onChange={onChange}
            />
        </div>
    );
}
